import { useState, useEffect, useCallback } from 'react';
import { 
  Upload, 
  Trash2, 
  Image as ImageIcon, 
  Video,
  Search,
  Filter,
  Grid,
  ZoomIn,
  X,
  Download,
  ExternalLink,
  CheckSquare,
  Link as LinkIcon,
  Plus
} from 'lucide-react';
import axios from 'axios';
import { showToast } from '../ui/Toast';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import Input from '../ui/Input';
import './GalleryModern.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

const GalleryModern = ({ getAuthHeader }) => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'image', 'video'
  const [selectedItems, setSelectedItems] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  
  // New states for URL input
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlData, setUrlData] = useState({ url: '', title: '', type: 'video' });

  useEffect(() => {
    loadGalleryItems();
  }, []);

  const loadGalleryItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/gallery`, getAuthHeader());
      setGalleryItems(response.data);
    } catch (error) {
      showToast.error('❌ Erreur lors du chargement de la galerie');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    const fileArray = Array.from(files);
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    
    for (let index = 0; index < fileArray.length; index++) {
      const file = fileArray[index];
      const tempId = `temp-${Date.now()}-${index}`;
      
      // Validate file size
      if (file.size > MAX_SIZE) {
        showToast.error(`❌ ${file.name} est trop volumineux (max 10MB)`);
        continue;
      }
      
      setUploadingFiles(prev => [...prev, { id: tempId, name: file.name, progress: 0 }]);

      try {
        const type = file.type.startsWith('video/') ? 'video' : 'image';
        
        // Upload image/video file to server
        const formData = new FormData();
        formData.append('file', file);
        
        const authHeaders = getAuthHeader();
        const uploadResponse = await axios.post(
          `${BACKEND_URL}/api/admin/upload-image`,
          formData,
          {
            headers: {
              ...authHeaders.headers,
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent) => {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadingFiles(prev => 
                prev.map(f => f.id === tempId ? { ...f, progress: Math.min(progress, 90) } : f)
              );
            },
          }
        );
        
        const uploadedUrl = uploadResponse.data.url;
        
        // Create gallery item with the uploaded URL
        const newItem = {
          type,
          url: uploadedUrl,
          title: file.name,
          order: galleryItems.length + index
        };

        await axios.post(`${API}/gallery`, newItem, getAuthHeader());
        
        setUploadingFiles(prev => 
          prev.map(f => f.id === tempId ? { ...f, progress: 100 } : f)
        );

        setTimeout(() => {
          setUploadingFiles(prev => prev.filter(f => f.id !== tempId));
        }, 1000);

        await loadGalleryItems();
        showToast.success(`✅ ${file.name} ajouté avec succès !`);
      } catch (error) {
        console.error('Upload error:', error);
        setUploadingFiles(prev => prev.filter(f => f.id !== tempId));
        showToast.error(`❌ Erreur lors de l'upload de ${file.name}`);
      }
    }
  };

  const handleUrlSubmit = async () => {
    if (!urlData.url) {
      showToast.warning('⚠️ Veuillez entrer une URL');
      return;
    }

    try {
      const newItem = {
        type: urlData.type,
        url: urlData.url,
        title: urlData.title || 'Lien externe',
        order: galleryItems.length
      };

      await axios.post(`${API}/gallery`, newItem, getAuthHeader());
      await loadGalleryItems();
      showToast.success('✅ Lien ajouté avec succès !');
      setShowUrlInput(false);
      setUrlData({ url: '', title: '', type: 'video' });
    } catch (error) {
      showToast.error('❌ Erreur lors de l\'ajout du lien');
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  }, [galleryItems]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) return;

    try {
      await axios.delete(`${API}/gallery/${id}`, getAuthHeader());
      await loadGalleryItems();
      showToast.success('🗑️ Élément supprimé avec succès');
    } catch (error) {
      showToast.error('❌ Erreur lors de la suppression');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedItems.length} éléments ?`)) return;

    try {
      await Promise.all(
        selectedItems.map(id => axios.delete(`${API}/gallery/${id}`, getAuthHeader()))
      );
      await loadGalleryItems();
      setSelectedItems([]);
      showToast.success(`🗑️ ${selectedItems.length} éléments supprimés`);
    } catch (error) {
      showToast.error('❌ Erreur lors de la suppression en masse');
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.length === filteredItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredItems.map(item => item.id));
    }
  };

  const handleSelectItem = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  // Filtered items
  const filteredItems = galleryItems.filter(item => {
    const matchesSearch = !searchQuery || 
      (item.title && item.title.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesType = filterType === 'all' || item.type === filterType;
    return matchesSearch && matchesType;
  });

  const imageCount = galleryItems.filter(item => item.type === 'image').length;
  const videoCount = galleryItems.filter(item => item.type === 'video').length;

  const isYouTubeUrl = (url) => {
    return url && (url.includes('youtube.com') || url.includes('youtu.be'));
  };

  if (loading) {
    return <div className="gallery-loading">Chargement...</div>;
  }

  return (
    <div className="gallery-modern">
      {/* Header */}
      <div className="gallery-header">
        <div>
          <h1 className="gallery-title">Galerie Média</h1>
          <p className="gallery-subtitle">
            {filteredItems.length} éléments affichés sur {galleryItems.length} au total
          </p>
        </div>
        <div className="gallery-stats-quick">
          <Badge variant="primary" icon={<ImageIcon size={14} />}>
            {imageCount} images
          </Badge>
          <Badge variant="secondary" icon={<Video size={14} />}>
            {videoCount} vidéos
          </Badge>
        </div>
      </div>

      {/* Upload Zone */}
      <Card 
        className="upload-zone-card"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <div className="upload-zone">
          <Upload size={48} className="upload-icon" />
          <h3>Glissez-déposez vos fichiers ici</h3>
          <p>ou utilisez les options ci-dessous</p>
          <div className="upload-actions">
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={(e) => handleFileUpload(e.target.files)}
              style={{ display: 'none' }}
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button variant="primary" icon={<Upload size={18} />}>
                Upload Fichiers
              </Button>
            </label>
            <Button 
              variant="secondary" 
              icon={<LinkIcon size={18} />}
              onClick={() => setShowUrlInput(!showUrlInput)}
            >
              Ajouter URL/YouTube
            </Button>
          </div>
          <p className="upload-hint">Images (JPG, PNG, GIF), Vidéos (MP4, WebM) ou Liens YouTube</p>
        </div>
      </Card>

      {/* URL Input Form */}
      {showUrlInput && (
        <Card className="url-input-card" variant="primary">
          <h4>🔗 Ajouter un lien externe (YouTube, etc.)</h4>
          <div className="url-input-form">
            <Input
              label="URL"
              placeholder="https://www.youtube.com/watch?v=..."
              value={urlData.url}
              onChange={(e) => setUrlData({ ...urlData, url: e.target.value })}
              leftIcon={<LinkIcon size={18} />}
            />
            <Input
              label="Titre"
              placeholder="Nom de la vidéo/image"
              value={urlData.title}
              onChange={(e) => setUrlData({ ...urlData, title: e.target.value })}
            />
            <div className="form-group">
              <label>Type de média</label>
              <div className="type-selector">
                <button
                  className={`type-btn ${urlData.type === 'video' ? 'active' : ''}`}
                  onClick={() => setUrlData({ ...urlData, type: 'video' })}
                >
                  <Video size={16} /> Vidéo
                </button>
                <button
                  className={`type-btn ${urlData.type === 'image' ? 'active' : ''}`}
                  onClick={() => setUrlData({ ...urlData, type: 'image' })}
                >
                  <ImageIcon size={16} /> Image
                </button>
              </div>
            </div>
            <div className="url-form-actions">
              <Button
                variant="primary"
                icon={<Plus size={16} />}
                onClick={handleUrlSubmit}
              >
                Ajouter
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowUrlInput(false);
                  setUrlData({ url: '', title: '', type: 'video' });
                }}
              >
                Annuler
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Upload Progress */}
      {uploadingFiles.length > 0 && (
        <Card className="upload-progress-card">
          <h4>Upload en cours...</h4>
          {uploadingFiles.map(file => (
            <div key={file.id} className="upload-progress-item">
              <span className="upload-filename">{file.name}</span>
              <div className="upload-progress-bar">
                <div 
                  className="upload-progress-fill" 
                  style={{ width: `${file.progress}%` }}
                />
              </div>
              <span className="upload-percentage">{file.progress}%</span>
            </div>
          ))}
        </Card>
      )}

      {/* Toolbar */}
      <Card className="gallery-toolbar">
        <div className="toolbar-row">
          <div className="toolbar-search">
            <Input
              placeholder="Rechercher par titre..."
              leftIcon={<Search size={18} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="toolbar-actions">
            <Button
              variant="secondary"
              size="small"
              icon={<Filter size={18} />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filtres
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="filters-panel">
            <div className="filter-group">
              <label>Type de média</label>
              <div className="filter-buttons">
                <button
                  className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
                  onClick={() => setFilterType('all')}
                >
                  Tous ({galleryItems.length})
                </button>
                <button
                  className={`filter-btn ${filterType === 'image' ? 'active' : ''}`}
                  onClick={() => setFilterType('image')}
                >
                  <ImageIcon size={16} /> Images ({imageCount})
                </button>
                <button
                  className={`filter-btn ${filterType === 'video' ? 'active' : ''}`}
                  onClick={() => setFilterType('video')}
                >
                  <Video size={16} /> Vidéos ({videoCount})
                </button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Bulk Actions */}
      {selectedItems.length > 0 && (
        <Card className="bulk-actions-bar" variant="primary">
          <div className="bulk-actions-content">
            <div className="bulk-actions-info">
              <CheckSquare size={20} />
              <span>{selectedItems.length} élément(s) sélectionné(s)</span>
            </div>
            <div className="bulk-actions-buttons">
              <Button
                variant="danger"
                size="small"
                icon={<Trash2 size={16} />}
                onClick={handleBulkDelete}
              >
                Supprimer tout
              </Button>
              <Button
                variant="ghost"
                size="small"
                onClick={() => setSelectedItems([])}
              >
                Annuler
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Select All */}
      {filteredItems.length > 0 && (
        <div className="select-all-bar">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={selectedItems.length === filteredItems.length}
              onChange={handleSelectAll}
            />
            <span>Sélectionner tout</span>
          </label>
        </div>
      )}

      {/* Gallery Grid */}
      <div className="gallery-grid">
        {filteredItems.length > 0 ? (
          filteredItems.map(item => (
            <Card 
              key={item.id} 
              className={`gallery-item ${selectedItems.includes(item.id) ? 'gallery-item--selected' : ''}`}
              hover
            >
              <label className="gallery-checkbox">
                <input
                  type="checkbox"
                  checked={selectedItems.includes(item.id)}
                  onChange={() => handleSelectItem(item.id)}
                />
              </label>

              {item.type === 'image' ? (
                <div className="gallery-image-wrapper">
                  <img 
                    src={item.url} 
                    alt={item.title || 'Gallery item'}
                    className="gallery-image"
                    onClick={() => setLightboxImage(item.url)}
                  />
                  <button 
                    className="gallery-zoom-btn"
                    onClick={() => setLightboxImage(item.url)}
                  >
                    <ZoomIn size={20} />
                  </button>
                </div>
              ) : (
                <div className="gallery-video-wrapper">
                  {isYouTubeUrl(item.url) ? (
                    <>
                      <Video size={48} className="video-icon" />
                      <p className="youtube-label">🔗 Lien YouTube</p>
                    </>
                  ) : (
                    <>
                      <Video size={48} className="video-icon" />
                      <p>Vidéo</p>
                    </>
                  )}
                </div>
              )}

              <div className="gallery-item-info">
                <h4>{item.title || 'Sans titre'}</h4>
                <div className="gallery-item-badges">
                  <Badge variant={item.type === 'image' ? 'primary' : 'secondary'} size="small">
                    {item.type === 'image' ? 'Image' : 'Vidéo'}
                  </Badge>
                  {isYouTubeUrl(item.url) && (
                    <Badge variant="info" size="small" icon={<ExternalLink size={12} />}>
                      YouTube
                    </Badge>
                  )}
                </div>
              </div>

              <div className="gallery-item-actions">
                <Button
                  variant="danger"
                  size="small"
                  icon={<Trash2 size={16} />}
                  onClick={() => handleDelete(item.id)}
                >
                  Supprimer
                </Button>
              </div>
            </Card>
          ))
        ) : (
          <Card className="gallery-empty">
            <ImageIcon size={64} className="empty-icon" />
            <p>Aucun élément dans la galerie</p>
            <p className="empty-hint">Commencez par uploader des images/vidéos ou ajouter des liens YouTube</p>
          </Card>
        )}
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={() => setLightboxImage(null)}>
          <button className="lightbox-close" onClick={() => setLightboxImage(null)}>
            <X size={32} />
          </button>
          <img src={lightboxImage} alt="Preview" className="lightbox-image" />
        </div>
      )}
    </div>
  );
};

export default GalleryModern;
